import { mapFromKey } from 'constants/ggVars';

import * as React from 'react';
import { useEffect } from 'react';

import { useQuery } from '@apollo/client';
import FpDecompByReportIdAndProductIDQuery from '@graphql/queries/FpDecompByReportIdAndProductIDQuery';
import ProjectByIdQuery from '@graphql/queries/ProjectByIdQuery';
import ReportGgVarTuningPlot from '@graphql/queries/ReportGgVarTuningPlot';
import ReportProductsByReportIdQuery, {
  ReportProductsByReportIdResponse,
} from '@graphql/queries/ReportProductsByReportIdQuery';
import ReportRefFlavorTuningPlot from '@graphql/queries/ReportRefFlavorTuningPlot';
import { KeyboardBackspace as KeyboardBackspaceIcon } from '@mui/icons-material';
import { IconButton, Grid } from '@mui/material';
import { getProductName } from 'components/Report/utils';
import { QaPreviewReportProvider } from 'hooks/QaPreviewReportContext';
import useCustomProductNames from 'hooks/useCustomProductNames';
import useMinMaxPq from 'hooks/useMixMaxPq';
import usePqTransform from 'hooks/usePqTransform';
import useQaPreviewSession from 'hooks/useQaPreviewSession';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RouterProps } from 'react-router';
import { useLocation } from 'react-router-dom';
import { getQaPreviewSession } from 'utils/qaPreviewSession';

import LoadingScreen from '../../../components/LoadingScreen';
import FlavorSummaryChartGroup from '../../../components/Report/FlavorIntensityCharts/FlavorSummaryChartGroup';
import {
  PROJECT_SUMMARY_WITH_REPORT,
  REPORT_PRODUCT_SUMMARY,
} from '../../../constants/routePaths';
import selectWorkspaceProducerId from '../../../selectors/workspaceProducerId';
import { ProjectTabs } from '../../../services/customerPreferences';
import { PageHeader } from '../../../styles/themeComponents';
import { checkIsWrongWorkspace } from '../../../utils/afsUtils';
import formatPath from '../../../utils/formatPath';
import styles from '../ProjectSummary.module.css';

// Clean the version param, as it might be the null string
export const cleanVersionParam = (versionParam: string): string => {
  if (!!versionParam && versionParam != 'null') {
    return decodeURI(versionParam);
  } else {
    return null;
  }
};

interface Props extends RouterProps {
  match: Record<string, any>;
  location: Record<string, any>;
}

const ProjectFlavorSummaryChart: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const pqTransform = usePqTransform();
  const location = useLocation();
  const { state }: any = location;
  const { match, history } = props;

  const projectId: number | undefined = isNaN(parseInt(match.params.projectId))
    ? undefined
    : parseInt(match.params.projectId);
  const reportId: string = match.params.reportId;

  const workspaceId = useSelector((state) => selectWorkspaceProducerId(state));

  // QA reviewers previewing an unapproved report bypass the project/workspace check
  const qaPreview = useQaPreviewSession(reportId, projectId);

  const { data: projectCheck, loading: projectCheckLoading } =
    useQuery<reports.ProjectByIdResponse>(ProjectByIdQuery, {
      variables: { projectId },
      skip: projectId === undefined || !!qaPreview,
    });

  const productId: number = isNaN(parseInt(match.params.productId))
    ? null
    : parseInt(match.params.productId);
  const version: string = cleanVersionParam(match.params.version);
  const ggVarKey: string = match.params.ggVarKey;

  const { data: reportProductsData, loading: reportProductsLoading } =
    useQuery<ReportProductsByReportIdResponse>(ReportProductsByReportIdQuery, {
      variables: { reportId },
      skip:
        !reportId ||
        (!qaPreview &&
          (projectCheckLoading ||
            checkIsWrongWorkspace(
              projectId,
              projectCheck,
              workspaceId,
              reportId,
            ))),
    });

  const isWrongWorkspace =
    reportProductsLoading ||
    (!qaPreview &&
      checkIsWrongWorkspace(
        projectId,
        projectCheck,
        workspaceId,
        reportId,
        productId,
        reportProductsData,
      ));

  const skipDataQueries = isWrongWorkspace || projectCheckLoading;

  const customProductNames = useCustomProductNames({
    projectId,
    skip: isWrongWorkspace || projectCheckLoading,
    previewSession: qaPreview,
  });

  const {
    data: ggVarData,
    loading: ggVarLoading,
    error: ggVarError,
  } = useQuery<reports.GgVarTuningPlotResponse>(ReportGgVarTuningPlot, {
    variables: {
      reportID: reportId,
      productID: productId,
      ggVar: ggVarKey,
    },
    skip: skipDataQueries,
  });

  const {
    data: refData,
    loading: refLoading,
    error: refError,
  } = useQuery<reports.RefFlavorTuningPlotResponse>(ReportRefFlavorTuningPlot, {
    variables: {
      reportID: reportId,
      productID: productId,
      ggVar: ggVarKey,
    },
    skip: skipDataQueries,
  });

  const {
    data: decompData,
    loading: decompLoading,
    error: decompError,
  } = useQuery<reports.ReportFpDecompResponse>(
    FpDecompByReportIdAndProductIDQuery,
    {
      variables: {
        reportID: reportId,
        productID: productId,
      },
      skip: skipDataQueries,
    },
  );

  const {
    data: minMaxPqData,
    loading: minMaxPqLoading,
    error: minMaxPqError,
  } = useMinMaxPq(reportId, skipDataQueries);

  useEffect(() => {
    return () => {
      history.listen(() => {
        window.scrollTo(0, 0);
      });

      if (
        history.action === 'POP' &&
        reportId &&
        projectId !== undefined &&
        !getQaPreviewSession(reportId, projectId)
      ) {
        state && state.fromFlavorSummary
          ? history.replace({
              pathname: formatPath(PROJECT_SUMMARY_WITH_REPORT, {
                projectId,
                reportId,
              }),
              state: {
                productId,
                version,
                tabIdx: ProjectTabs.FLAVOR_SUMMARY,
              },
            })
          : history.replace({
              pathname: formatPath(REPORT_PRODUCT_SUMMARY, {
                projectId,
                reportId,
                productId,
                version,
              }),
            });
      }
    };
  }, []);

  if (projectCheckLoading || reportProductsLoading) {
    return <LoadingScreen />;
  }

  if (isWrongWorkspace) {
    return (
      <div>
        <h2>{t('errors.accessForbidden', 'Access Forbidden')}</h2>
        <p>
          {t(
            'errors.noProjectAccess',
            'You do not have access to view this project. Please ensure you are in the correct workspace.',
          )}
        </p>
      </div>
    );
  }

  if (
    !reportId ||
    !ggVarKey ||
    ggVarLoading ||
    refLoading ||
    decompLoading ||
    decompError ||
    minMaxPqLoading ||
    minMaxPqError
  ) {
    return <LoadingScreen />;
  }

  function filterRefFlavorTuningData(
    data: reports.RefFlavorTuningPlotResponse,
    version: string,
  ): reports.RefFlavorTuningPlotRow[] {
    return data.allRpRefFlavorDecomps.nodes.filter(
      (row) => row.version === version,
    );
  }

  function filterGgVarTuningData(
    data: reports.GgVarTuningPlotResponse,
    version: string,
  ): reports.GgVarTuningPlotRow[] {
    return data.allRpGgvarFlavorProfiles.nodes.filter(
      (row) => row.version === version,
    );
  }

  function filterDecompData(
    data: reports.ReportFpDecompResponse,
    ggVarKey: string,
  ): reports.ReportFpDecompRow[] {
    return data.allRpFlavorDecomps.nodes.filter(
      (row) => row.ggVars === ggVarKey,
    );
  }

  return (
    <QaPreviewReportProvider session={qaPreview}>
      <div>
        <div className={styles.tabContainer}>
          <Grid container>
            <Grid item xs={12}>
              <div className={styles.header}>
                <IconButton
                  onClick={history.goBack}
                  size="small"
                  style={{ marginLeft: -26 }}
                >
                  <KeyboardBackspaceIcon fontSize="small" />
                  <PageHeader>
                    {t('projects.projectFlavorSummary.back')}
                  </PageHeader>
                </IconButton>
              </div>
            </Grid>
            <Grid item xs={12}>
              <h4>
                {getProductName({
                  productNames: customProductNames,
                  productId: productId,
                  version,
                })}
              </h4>
            </Grid>
            <Grid container item xs={12} spacing={2}>
              {ggVarData && refData && (
                <FlavorSummaryChartGroup
                  ggVar={mapFromKey(ggVarKey)}
                  showDivider={false}
                  refFlavorTuningData={filterRefFlavorTuningData(
                    refData,
                    version,
                  )}
                  ggVarTuningData={filterGgVarTuningData(ggVarData, version)}
                  decompData={filterDecompData(decompData, ggVarKey)}
                  minPq={pqTransform.transform(minMaxPqData.minPq)}
                  maxPq={pqTransform.transform(minMaxPqData.maxPq)}
                />
              )}
            </Grid>
          </Grid>
        </div>
      </div>
    </QaPreviewReportProvider>
  );
};

export default ProjectFlavorSummaryChart;
